import { useEffect } from 'react';

function ensureMeta(selector, attributes) {
    let element = document.head.querySelector(selector);

    if (!element) {
        element = document.createElement('meta');
        Object.entries(attributes).forEach(([key, value]) => {
            element.setAttribute(key, value);
        });
        document.head.appendChild(element);
    }

    return element;
}

function Seo({
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogImageAlt,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterImageAlt,
}) {
    useEffect(() => {
        const previousTitle = document.title;
        const canonicalLink = document.head.querySelector('link[rel="canonical"]');
        const previousCanonical = canonicalLink?.getAttribute('href') ?? null;

        const managedElements = [
            {
                element: ensureMeta('meta[name="description"]', { name: 'description' }),
                nextValue: description,
            },
            {
                element: ensureMeta('meta[property="og:title"]', { property: 'og:title' }),
                nextValue: ogTitle,
            },
            {
                element: ensureMeta('meta[property="og:description"]', { property: 'og:description' }),
                nextValue: ogDescription,
            },
            {
                element: ensureMeta('meta[property="og:url"]', { property: 'og:url' }),
                nextValue: canonical,
            },
            {
                element: ensureMeta('meta[property="og:image"]', { property: 'og:image' }),
                nextValue: ogImage,
            },
            {
                element: ensureMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }),
                nextValue: ogImageAlt,
            },
            {
                element: ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }),
                nextValue: twitterTitle,
            },
            {
                element: ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }),
                nextValue: twitterDescription,
            },
            {
                element: ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image' }),
                nextValue: twitterImage,
            },
            {
                element: ensureMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }),
                nextValue: twitterImageAlt,
            },
        ].map(({ element, nextValue }) => ({
            element,
            previousValue: element.getAttribute('content'),
            nextValue,
        }));

        document.title = title;

        if (canonicalLink) {
            canonicalLink.setAttribute('href', canonical);
        }

        managedElements.forEach(({ element, nextValue }) => {
            element.setAttribute('content', nextValue);
        });

        return () => {
            document.title = previousTitle;

            if (canonicalLink && previousCanonical) {
                canonicalLink.setAttribute('href', previousCanonical);
            }

            managedElements.forEach(({ element, previousValue }) => {
                if (previousValue != null) {
                    element.setAttribute('content', previousValue);
                }
            });
        };
    }, [
        canonical,
        description,
        ogDescription,
        ogImage,
        ogImageAlt,
        ogTitle,
        title,
        twitterDescription,
        twitterImage,
        twitterImageAlt,
        twitterTitle,
    ]);

    return null;
}

export default Seo;
